import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `explorer:` scope (F7/F6), following the
 * exact `documents:` precedent (`apps/web/src/routes/documents/
 * documents.i18n.ts`) — NOT a second i18n runtime (E18/§13.3 ban), just
 * physically scoped here because `packages/i18n/**` is outside this task's
 * ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/explorer.json`.
 */
const en = {
  'title': 'Explorer',
  'eyebrow': 'My files',
  'action.upload': 'Upload',
  'action.connectDrive': 'Connect Drive',
  'action.newFolder': 'New folder',
  'breadcrumb.home': 'Home',
  'breadcrumb.allFiles': 'All files',
  'column.name': 'Name',
  'column.kind': 'Type',
  'column.size': 'Size',
  'column.updatedAt': 'Updated',
  'column.status': 'Status',
  'column.actions': 'Actions',
  /** mockup-22: card badge showing where a file came from (design/app/22-explorer.html `f.badge`). */
  'column.origin': 'Origin',
  'searchLabel': 'Search files and folders',
  'searchPlaceholder': 'Search this folder…',
  'empty.title': 'No files yet',
  'empty.description': 'Upload documents or connect Google Drive to start building your library.',
  'empty.cta': 'Upload a file',
  'noResults.title': 'Nothing matches “{query}”',
  'error.title': 'Couldn’t load this folder.',
  'kind.folder': 'Folder',
  'kind.upload': 'Document',
  'kind.source': 'Source text',
  'kind.render': 'Rendered file',
  'kind.illustration': 'Illustration',
  'kind.cover': 'Cover',
  'kind.bundle': 'Bundle',
  'kind.thumbnail': 'Thumbnail',
  'status.pending': 'Pending',
  'status.uploaded': 'Uploaded',
  'status.ingesting': 'Processing…',
  'status.ready': 'Ready',
  'status.failed': 'Failed',
  'provenance.uploaded': 'Uploaded',
  'provenance.drive': 'From Drive',
  'provenance.generated': 'Generated',
  'action.open': 'Open',
  'action.rename': 'Rename',
  'action.move': 'Move',
  'action.delete': 'Delete',
  'action.download': 'Download',
  'action.cancel': 'Cancel',
  'action.save': 'Save',
  'action.create': 'Create',
  'toast.deleted.action': 'Deleted',
  'toast.deleted.undo': 'Undo',
  'toast.renamed.action': 'Renamed',
  'toast.moved.action': 'Moved',
  'toast.folderCreated.action': 'Created folder',
  'confirm.delete': 'Delete',
  'rename.title': 'Rename',
  'rename.label': 'Name',
  'newFolder.title': 'New folder',
  'newFolder.label': 'Folder name',
  'move.title': 'Move “{name}”',
  'move.description': 'Choose a destination folder.',
  'move.root': 'Home',
  'move.empty': 'No folders here.',
  'move.confirm': 'Move here',
  'preview.title': 'Preview',
  'preview.details': 'Details',
  'preview.unavailable.title': 'Preview unavailable',
  'preview.unavailable.description': 'This file type doesn’t have a preview yet — you can still download or delete it.',
  'preview.stillProcessing': 'Still processing — check back shortly.',
  'preview.meta.type': 'Type',
  'preview.meta.size': 'Size',
  'preview.meta.updated': 'Updated',
  'preview.meta.provenance': 'Source',
  'preview.downloadUnavailable': 'A direct download link isn’t available for this file yet.',
  'preview.close': 'Close preview',
} as const;

const ro: Record<keyof typeof en, string> = {
  'title': 'Explorator',
  'eyebrow': 'Fișierele mele',
  'action.upload': 'Încarcă',
  'action.connectDrive': 'Conectează Drive',
  'action.newFolder': 'Folder nou',
  'breadcrumb.home': 'Acasă',
  'breadcrumb.allFiles': 'Toate fișierele',
  'column.name': 'Nume',
  'column.kind': 'Tip',
  'column.size': 'Dimensiune',
  'column.updatedAt': 'Actualizat',
  'column.status': 'Stare',
  'column.actions': 'Acțiuni',
  'column.origin': 'Origine',
  'searchLabel': 'Caută fișiere și foldere',
  'searchPlaceholder': 'Caută în acest folder…',
  'empty.title': 'Niciun fișier încă',
  'empty.description': 'Încarcă documente sau conectează Google Drive pentru a-ți construi biblioteca.',
  'empty.cta': 'Încarcă un fișier',
  'noResults.title': 'Nimic nu se potrivește cu „{query}”',
  'error.title': 'Nu am putut încărca acest folder.',
  'kind.folder': 'Folder',
  'kind.upload': 'Document',
  'kind.source': 'Text sursă',
  'kind.render': 'Fișier randat',
  'kind.illustration': 'Ilustrație',
  'kind.cover': 'Copertă',
  'kind.bundle': 'Pachet',
  'kind.thumbnail': 'Miniatură',
  'status.pending': 'În așteptare',
  'status.uploaded': 'Încărcat',
  'status.ingesting': 'Se procesează…',
  'status.ready': 'Gata',
  'status.failed': 'Eșuat',
  'provenance.uploaded': 'Încărcat',
  'provenance.drive': 'Din Drive',
  'provenance.generated': 'Generat',
  'action.open': 'Deschide',
  'action.rename': 'Redenumește',
  'action.move': 'Mută',
  'action.delete': 'Șterge',
  'action.download': 'Descarcă',
  'action.cancel': 'Anulează',
  'action.save': 'Salvează',
  'action.create': 'Creează',
  'toast.deleted.action': 'Șters',
  'toast.deleted.undo': 'Anulează',
  'toast.renamed.action': 'Redenumit',
  'toast.moved.action': 'Mutat',
  'toast.folderCreated.action': 'Folder creat',
  'confirm.delete': 'Șterge',
  'rename.title': 'Redenumește',
  'rename.label': 'Nume',
  'newFolder.title': 'Folder nou',
  'newFolder.label': 'Numele folderului',
  'move.title': 'Mută „{name}”',
  'move.description': 'Alege un folder de destinație.',
  'move.root': 'Acasă',
  'move.empty': 'Niciun folder aici.',
  'move.confirm': 'Mută aici',
  'preview.title': 'Previzualizare',
  'preview.details': 'Detalii',
  'preview.unavailable.title': 'Previzualizare indisponibilă',
  'preview.unavailable.description': 'Acest tip de fișier nu are încă o previzualizare — îl poți totuși descărca sau șterge.',
  'preview.stillProcessing': 'Încă se procesează — revino în curând.',
  'preview.meta.type': 'Tip',
  'preview.meta.size': 'Dimensiune',
  'preview.meta.updated': 'Actualizat',
  'preview.meta.provenance': 'Sursă',
  'preview.downloadUnavailable': 'Un link de descărcare directă nu este încă disponibil pentru acest fișier.',
  'preview.close': 'Închide previzualizarea',
};

export const EXPLORER_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type ExplorerCatalogKey = keyof typeof en;
